import { EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, List, Modal, ModalProps, Typography } from 'antd'
import dayjs from 'dayjs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  useTemplatesServiceDeleteSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey
} from 'src/api/queries'
import { ApiError, ContentType, TemplateItemDto } from 'src/api/requests'
import { DeleteConfirm, message } from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: () => void
}

export const DraftConsultationsModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const params = useParams()
  const personId = params.personId ?? params.id
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const isPatient = pathname.includes('patients')

  const { userData } = useUser()

  const { data, isLoading } = useTemplatesServiceGetSystemTemplateListEndpoint({
    type: [ContentType.CONSULTATION_DRAFT],
    institutionIds: userData?.currentLegalEntityId
      ? [userData?.currentLegalEntityId]
      : [],
    pageSize: 50
  })

  const { mutate: deleteTemplate } =
    useTemplatesServiceDeleteSystemTemplateEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
        })
        message.success(t('consultations.draft_deleted'))
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const onSelect = (draftId: string) => {
    if (personId) {
      navigate(
        isPatient
          ? paths.patients.consultations.draft(personId, draftId)
          : paths.persons.consultations.draft(personId, draftId)
      )
      onCancel()
    }
  }

  return (
    <Modal
      title={t('consultations.select_draft')}
      open
      maskClosable={false}
      onCancel={onCancel}
      footer={null}
      width={600}
      {...props}
    >
      <List
        loading={isLoading}
        dataSource={data?.data ?? []}
        locale={{ emptyText: t('consultations.no_drafts') }}
        renderItem={(item: TemplateItemDto) => (
          <List.Item
            actions={[
              <Button
                key="edit"
                type="link"
                icon={<EditOutlined />}
                onClick={() => onSelect(String(item.id))}
              />,
              <DeleteConfirm
                key="delete"
                title={t('common.delete')}
                onConfirm={() => deleteTemplate({ id: String(item.id) })}
              />
            ]}
          >
            <Flex vertical>
              <Typography.Text strong>{item.name}</Typography.Text>
              {item.createdDate && (
                <Typography.Text type="secondary">
                  {dayjs(item.createdDate).format(dayMonthYearFormatWithDot)}
                </Typography.Text>
              )}
            </Flex>
          </List.Item>
        )}
      />
    </Modal>
  )
}
